import { Checkmark, ChevronDown, ChevronUp } from '@carbon/icons-react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { type SelectContentProps } from '@radix-ui/react-select'
import { cx } from 'lib/cx'
import React, { useState } from 'react'

type Props = {
  children?: React.ReactNode
  placeholder?: string
  label: string
  icon?: React.ReactNode
  className?: string
  IconClassName?: string
  contentStyle?: React.CSSProperties
  onCloseAutoFocus?: SelectContentProps['onCloseAutoFocus']
  Trigger?: React.ComponentType<TriggerProps>
} & SelectPrimitive.SelectProps

export default function Select({
  children,
  placeholder = '',
  label,
  icon,
  className,
  IconClassName,
  contentStyle,
  onCloseAutoFocus,
  Trigger = Select.DefaultTrigger,
  ...selectProps
}: Props) {
  const [open, setOpen] = useState(false)

  return (
    <SelectPrimitive.Root
      {...selectProps}
      onOpenChange={(open) => {
        setOpen(open)
        selectProps.onOpenChange?.(open)
      }}
    >
      <Trigger
        className={className}
        label={label}
        icon={icon}
        placeholder={placeholder}
        open={open}
        IconClassName={IconClassName}
      />
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="z-50 overflow-hidden bg-white shadow-md"
          style={contentStyle}
          position="popper"
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <SelectPrimitive.ScrollUpButton
            className={cx(
              'flex h-6 w-6 cursor-pointer items-center justify-center bg-white p-1 text-black transition-transform',
              IconClassName,
            )}
          >
            <ChevronUp className="h-4 w-4" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="viewport group divide-y">
            {children}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton
            className={cx(
              'flex h-6 w-6 cursor-pointer items-center justify-center bg-white p-1 text-black transition-transform',
              IconClassName,
            )}
          >
            <ChevronDown className="h-4 w-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

export type TriggerProps = {
  className?: string
  label: string
  icon?: React.ReactNode
  placeholder: string
  open?: boolean
  IconClassName?: string
}

Select.DefaultTrigger = function SelectDefaultTrigger({
  className,
  label,
  icon,
  placeholder,
  open = false,
  IconClassName,
}: TriggerProps) {
  return (
    <SelectPrimitive.Trigger
      className={cx('button hover:bg-gray-20', className)}
      aria-label={label}
    >
      <div className="flex min-w-0 items-center gap-2">
        {icon}
        <div className="line-clamp-1">
          <SelectPrimitive.SelectValue
            className="!font-normal"
            placeholder={placeholder}
          />
        </div>
      </div>
      <SelectPrimitive.Icon
        className={cx(
          'h-6 w-6 cursor-pointer p-1 text-black transition-transform',
          IconClassName,
        )}
      >
        {open ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

Select.Trigger = SelectPrimitive.Trigger
Select.Icon = SelectPrimitive.Icon

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectItem({ children, className, ...props }, forwardedRef) {
  return (
    <SelectPrimitive.Item
      className={cx(
        'data-[highlighted]:bg-gray-10 relative flex cursor-pointer items-center p-4 pr-[35px] select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:text-black data-[highlighted]:outline-hidden md:text-sm',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-3 inline-flex w-4 items-center justify-center">
        <Checkmark />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
})
